import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContextMenu } from '../context/ContextMenuContext';
import ContextMenu from '../context-menu/ContextMenu';
import ContextMenuItem from '../context-menu/ContextMenuItem';
import { useOffline } from '../context/OfflineContext';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import Popup from '../popup/Popup';
import EventStatus, { eventStatusLabel } from '../status/EventStatus';
import ActionButton from '../input/ActionButton';
import CustomTogglebox from '../input/CustomTogglebox';
import AddOnChips from '../add-on/AddOnChips';
import LocationSelect from '../location/LocationSelect';
import ParticipantGroupPriceMatrix, {
  matrixRowsToInputs, settingsToMatrixRows, validateMatrixRows,
} from '../participant-group/ParticipantGroupPriceMatrix';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { ParticipantGroupChip } from '../participant-group/ParticipantGroupChips';
import api from '../wrapper/API';
import useLocations from '../../hooks/useLocations';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { isAdmin } from '../../util/auth';
import { ROUTES } from '../../util/routes';
import { eventRules } from '../../util/eventRules';
import {
  compareParticipantGroups, isPersonalLinkGroup, releaseSummary,
} from '../../util/participantGroups';
import { formatEuro, samePrice } from '../../util/eventPrices';
import { addOnCounterItems, formatMax, priceFor } from '../../util/addOns';
import { mapLinkFor } from '../../util/location';
import { copyText } from '../../util/clipboard';
import '../upcoming-events/UpcomingEventsList.css';
import '../popup/EditPopup.css';
import '../form/Form.css';
import './EventContextMenu.css';

const EMPTY_EDIT = {
  eventId: null,
  date: '',
  eventLabel: '',
  linkSignup: false,
  neverPublic: false,
  // Im Prozess kommt der Ort vom Prozess — die Location ist dann gesperrt.
  inProcess: false,
  locationId: null,
  location: null,
  // Preis + Freigabe je Gruppe (ParticipantGroupPriceMatrix-Zeilen, Preise als Eingabe-Strings).
  hasSettings: false,
  rows: {},
  // Anmelde-Zusätze des Prozesses (EventAddOnSummary, nur Anzeige — gepflegt werden sie im Prozess).
  addOns: [],
  autoPaidOnPresence: false,
  memo: '',
};

const LINK_IDLE = { status: 'idle', url: '', error: '' };
const CANCEL_IDLE = { status: 'idle', eventId: null, data: null };
const ACTIVATE_IDLE = { eventId: null, keptIds: [], inProcess: false, requiresLeader: false };

function buildEditForm(post) {
  const rules = eventRules(post);
  const settings = Array.isArray(post.participantGroupSettings) ? post.participantGroupSettings : null;
  return {
    eventId: post.id,
    date: post.date ?? '',
    eventLabel: rules.eventLabel ?? '',
    linkSignup: rules.linkSignup,
    neverPublic: rules.neverPublic,
    inProcess: post.processId != null,
    locationId: post.locationId ?? post.location?.id ?? null,
    location: post.location ?? null,
    hasSettings: settings != null,
    rows: settingsToMatrixRows(settings),
    addOns: Array.isArray(post.addOns) ? post.addOns : [],
    autoPaidOnPresence: !!post.autoPaidOnPresence,
    memo: post.memo ?? '',
  };
}

// Gruppen der Event-Zeilen in Anzeige-Reihenfolge (archivierte Gruppen nur, wenn sie eine Zeile haben).
function groupsOfSettings(ids, groupsById) {
  return ids
    .map(id => groupsById.get(Number(id)))
    .filter(Boolean)
    .sort(compareParticipantGroups);
}

// Singular bei genau einer Person ("1 Gast"), sonst Plural ("3 Gäste").
const groupCountLabel = (count, group, fallbackName) => {
  if (!group) return `${count} × ${fallbackName}`;
  return `${count} ${count === 1 ? group.name : group.pluralName}`;
};

function formatDateDe(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('-');
  return y && m && d ? `${d.substring(0, 2)}.${m}.${y}` : String(dateStr);
}

function apiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.title) return data.title;
  if (!err?.response) return 'Keine Verbindung – bitte später erneut versuchen.';
  return fallback;
}

function EventContextMenu({ posts, setPosts, type, locationsApi }) {
  const { contextMenuData } = useContextMenu();
  const { openPopup, closePopup, isPopupOpen, popupData } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const navigate = useNavigate();
  // Eine geteilte Gruppenliste (Namen, Farben, Anmeldeweg) für Matrix, Info, Absage und Aktivieren.
  const { byId: groupsById, groups: allGroups, loaded: groupsLoaded, failed: groupsFailed } = useParticipantGroups();

  const [error, setError] = useState('');
  const [info, setInfo] = useState({ memo: '', date: '', state: 'Draft' });

  // ── Bearbeiten-Popup ──
  // Optional geteilte Location-Liste der Seite (/upcoming-events); sonst eigene Instanz (Home).
  const ownLocations = useLocations();
  const { locations, loading: locationsLoading, ensureLoaded: ensureLocationsLoaded, upsert: upsertLocation } = locationsApi ?? ownLocations;
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editOriginal, setEditOriginal] = useState(EMPTY_EDIT);
  const [editError, setEditError] = useState('');
  // null | 'preview' (PreviewUpdate läuft) | 'save' (Update läuft)
  const [busy, setBusy] = useState(null);
  // 'form' = Eingabe, 'confirm' = Bestätigung mit PreviewUpdate-Ergebnis (gleiches Popup, kein zweites).
  const [editStep, setEditStep] = useState('form');
  const [preview, setPreview] = useState(null); // { body, data: EventUpdatePreviewResponse }
  const editRequestRef = useRef(0);
  const editTopRef = useRef(null);

  // ── Einladungslink (nur Link-Anmeldung) ──
  const [link, setLink] = useState(LINK_IDLE);
  const [linkFeedback, setLinkFeedback] = useState(null); // { tone: 'success' | 'error', text }
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const linkRequestRef = useRef(0);
  const copiedTimerRef = useRef(null);

  // ── Absagen (CancelPreview) / Aktivieren ──
  const [cancelPreview, setCancelPreview] = useState(CANCEL_IDLE);
  const cancelRequestRef = useRef(0);
  const [activateState, setActivateState] = useState(ACTIVATE_IDLE);

  // Antworten können eintreffen, nachdem das Popup geschlossen/gewechselt wurde.
  const popupStateRef = useRef({ isPopupOpen, popupData });
  popupStateRef.current = { isPopupOpen, popupData };

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  // Schrittwechsel im Bearbeiten-Popup: Inhalt wieder von oben zeigen.
  useEffect(() => {
    editTopRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [editStep]);

  const { isOffline } = useOffline();
  const currentPost = posts.find(p => p.id === contextMenuData.identifier);
  const currentRules = eventRules(currentPost);
  const isCancelled = currentPost?.state === 'Cancelled' || currentPost?.state === 2;
  const isCompleted = currentPost?.state === 'Completed' || currentPost?.state === 3;
  // „Anmeldung schließen/öffnen" nur bei Events mit Link-Anmeldung, die noch stattfinden.
  const canToggleSignup = isAdmin() && !!currentPost && currentRules.linkSignup && !isCancelled && !isCompleted;

  // Plural-Namen der Gruppen mit persönlichem Link ("Mitglieder") für die Hinweistexte.
  const personalLinkNames = useMemo(
    () => allGroups.filter(g => !g.isArchived && isPersonalLinkGroup(g)).map(g => g.pluralName).join(', '),
    [allGroups],
  );

  const updatePost = (updated) => {
    setPosts(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
  };

  const setField = (key, value) => setEditForm(prev => ({ ...prev, [key]: value }));
  const setRow = (groupId, patch) => setEditForm(prev => ({
    ...prev,
    rows: { ...prev.rows, [groupId]: { ...prev.rows[groupId], ...patch } },
  }));

  const showEventInfo = () => {
    setInfo(currentPost);
    openPopup('event', currentPost.id);
  };

  // Link beim Öffnen laden, nicht erst beim Klick auf „Kopieren": ein await zwischen Klick und
  // Clipboard-Zugriff verliert die User-Geste, Safari lehnt das Kopieren dann ab. Bewusst ohne Cache.
  const loadLink = (eventId) => {
    const requestId = ++linkRequestRef.current;
    setLink({ status: 'loading', url: '', error: '' });
    api.get(`/PrivateEvent/${eventId}/Link`)
      .then(res => {
        if (linkRequestRef.current !== requestId) return;
        setLink({ status: 'ready', url: res.data?.url ?? '', error: '' });
      })
      .catch(err => {
        if (linkRequestRef.current !== requestId) return;
        setLink({ status: 'error', url: '', error: apiErrorMessage(err, 'Link konnte nicht geladen werden.') });
      });
  };

  const editEvent = () => {
    if (!currentPost) return;
    const form = buildEditForm(currentPost);
    editRequestRef.current += 1; // laufende Vorschau/Speicherung eines vorherigen Öffnens ignorieren
    setEditForm(form);
    setEditOriginal(form);
    setEditError('');
    setBusy(null);
    setEditStep('form');
    setPreview(null);
    setConfirmRegenerate(false);
    setRegenerating(false);
    setCopied(false);
    setLinkFeedback(null);
    // Im Prozess ist die Location gesperrt — die Liste wird nur gebraucht, wenn sie wählbar ist
    // (oder die eigene Location des Events fehlt).
    if (!form.inProcess || form.location == null) ensureLocationsLoaded();
    if (form.linkSignup) {
      loadLink(currentPost.id);
    } else {
      linkRequestRef.current += 1;
      setLink(LINK_IDLE);
    }
    openPopup('editevent', currentPost.id);
  };

  const selectRoundmail = () => {
    navigate(ROUTES.roundmail(currentPost.id));
  };

  const showAnalytics = () => {
    navigate(ROUTES.statisticsForEvent(currentPost.id));
  };

  const handlePermanentDelete = () => {
    startSuccessBarLoading("Spieltag wird gelöscht");
    api.delete(`/Event/Delete/${currentPost.id}`)
      .then(() => {
        startSuccessBarSuccess("Spieltag gelöscht");
        setPosts(prev => prev.filter(p => p.id !== currentPost.id));
      })
      .catch((err) => {
        closeSuccessBar();
        setError(err?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventerror", currentPost.id);
      });
    closePopup();
  };

  // ── Absagen ──
  // Empfänger der Absage-Mail beim Öffnen ermitteln (je Gruppe: Teilnehmer, mit E-Mail, Mail-Regel an/aus).
  const openCancelPopup = () => {
    if (!currentPost) return;
    const eventId = currentPost.id;
    const requestId = ++cancelRequestRef.current;
    setCancelPreview({ status: 'loading', eventId, data: null });
    api.get(`/Event/${eventId}/CancelPreview`)
      .then(res => {
        if (cancelRequestRef.current !== requestId) return;
        setCancelPreview({ status: 'ready', eventId, data: res.data });
      })
      .catch(() => {
        if (cancelRequestRef.current !== requestId) return;
        setCancelPreview({ status: 'error', eventId, data: null });
      });
    openPopup('confirmdeleteevent', eventId);
  };

  const handleConfirmDelete = () => {
    const eventId = cancelPreview.eventId ?? currentPost?.id;
    if (eventId == null) return;
    cancelRequestRef.current += 1;
    startSuccessBarLoading("Event wird abgesagt");
    api.put(`/Event/${eventId}/Cancel`)
      .then((response) => {
        startSuccessBarSuccess("Event abgesagt");
        updatePost(response.data);
      })
      .catch((err) => {
        closeSuccessBar();
        setError(err?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventerror", eventId);
      });
    closePopup();
  };

  // ── Aktivieren ──
  // Released groups stay released; releases of executed process steps are caught up by the backend.
  const openActivatePopup = () => {
    if (!currentPost) return;
    const settings = Array.isArray(currentPost.participantGroupSettings) ? currentPost.participantGroupSettings : [];
    const keptIds = settings.filter(s => s.released).map(s => Number(s.participantGroupId));
    setActivateState({
      eventId: currentPost.id,
      keptIds,
      inProcess: currentPost.processId != null,
      requiresLeader: currentRules.requiresLeader,
    });
    openPopup('confirmactivateevent', currentPost.id);
  };

  const handleConfirmActivate = () => {
    const { eventId } = activateState;
    if (eventId == null) return;
    startSuccessBarLoading("Event wird aktiviert");
    api.put(`/Event/${eventId}/Activate`)
      .then((response) => {
        startSuccessBarSuccess("Event aktiviert");
        updatePost(response.data);
      })
      .catch((err) => {
        closeSuccessBar();
        setError(apiErrorMessage(err, "Event konnte nicht aktiviert werden."));
        openPopup("eventerror", eventId);
      });
  };

  // Sperrt bzw. öffnet neue Anmeldungen über alle Kanäle (Einladungslink, persönliche Links).
  // Schließen läuft über die Rückfrage 'confirmclosesignup', Öffnen direkt aus dem Kontextmenü.
  const updateSignupClosed = (closed) => {
    const eventId = currentPost?.id;
    if (eventId == null) return;
    startSuccessBarLoading(closed ? "Anmeldung wird geschlossen" : "Anmeldung wird geöffnet");
    api.put(`/Event/Update/${eventId}`, { signupClosed: closed })
      .then((response) => {
        startSuccessBarSuccess(closed ? "Anmeldung geschlossen" : "Anmeldung geöffnet");
        if (response.data?.id != null) updatePost(response.data);
      })
      .catch((err) => {
        closeSuccessBar();
        setError(apiErrorMessage(err, "Anmeldestatus konnte nicht geändert werden."));
        openPopup("eventerror", eventId);
      });
  };

  const handleConfirmCloseSignup = () => {
    updateSignupClosed(true);
    closePopup();
  };

  // Location im Karten-Dialog gespeichert: Liste ergänzen und alle geladenen Spieltage mit dieser
  // Location lokal nachziehen (Name/Adresse/Koordinaten) — kein Neuladen nötig.
  const handleLocationSaved = (loc) => {
    upsertLocation(loc);
    const summary = { id: loc.id, name: loc.name, address: loc.address ?? null, latitude: loc.latitude, longitude: loc.longitude };
    setEditForm(prev => (prev.location?.id === loc.id ? { ...prev, location: summary } : prev));
    setPosts(prev => prev.map(p => ((p.locationId ?? p.location?.id) === loc.id ? { ...p, location: summary } : p)));
  };

  const handleCopyLink = () => {
    if (!link.url) return;
    copyText(link.url).then(ok => {
      clearTimeout(copiedTimerRef.current);
      if (ok) {
        setCopied(true);
        setLinkFeedback(null);
        copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
      } else {
        setCopied(false);
        setLinkFeedback({ tone: 'error', text: 'Kopieren nicht möglich – bitte den Link markieren und manuell kopieren.' });
      }
    });
  };

  const handleRegenerateLink = () => {
    if (regenerating || editForm.eventId == null) return;
    const requestId = ++linkRequestRef.current;
    setRegenerating(true);
    setLinkFeedback(null);
    api.post(`/PrivateEvent/${editForm.eventId}/RegenerateLink`)
      .then(res => {
        if (linkRequestRef.current !== requestId) return;
        setLink({ status: 'ready', url: res.data?.url ?? '', error: '' });
        setCopied(false);
        setConfirmRegenerate(false);
        setLinkFeedback({ tone: 'success', text: 'Neuer Link erzeugt – der bisherige ist ab sofort ungültig.' });
      })
      .catch(err => {
        if (linkRequestRef.current !== requestId) return;
        setLinkFeedback({ tone: 'error', text: apiErrorMessage(err, 'Neuer Link konnte nicht erzeugt werden.') });
      })
      .finally(() => {
        if (linkRequestRef.current === requestId) setRegenerating(false);
      });
  };

  // Matrix-Gruppen des geöffneten Events (Zeilen inkl. virtueller Standard-Zeilen).
  const editGroups = useMemo(
    () => groupsOfSettings(Object.keys(editOriginal.rows), groupsById),
    [editOriginal.rows, groupsById],
  );

  // Gesperrte Freigabe-Schalter. Eine schon bestehende Freigabe bleibt umschaltbar (zurücknehmen).
  const editReleaseLockedReason = (group) => {
    if (editOriginal.rows[group.id]?.released) return null;
    if (group.isGuest) return editForm.neverPublic ? 'Nie öffentlich (Vorlage)' : null;
    if (group.signupChannel === 'Form') return 'Anmeldung per Formular – sieht, was Gäste sehen';
    if (group.isArchived) return 'Gruppe archiviert';
    return null;
  };

  // Body für /Event/Update bzw. /PreviewUpdate — nur geänderte Felder (alle optional), bei den Gruppen nur
  // geänderte Zeilen. null = Eingabefehler (editError ist gesetzt).
  // Zusätze sind nicht Teil des Spieltags (sie gehören zum Prozess) — die Matrix kennt nur Eintritt + Freigabe.
  const buildUpdateBody = () => {
    const original = editOriginal;
    // Im Prozess ist die Location gesperrt (kommt vom Prozess) — dort nie prüfen oder senden.
    const locationEditable = !original.inProcess;
    if (locationEditable && editForm.locationId == null) {
      setEditError('Bitte eine Location wählen.');
      return null;
    }
    const matrixError = validateMatrixRows(editForm.rows, editGroups);
    if (matrixError) {
      setEditError(matrixError);
      return null;
    }

    const body = {};
    if (locationEditable && editForm.locationId !== original.locationId) body.locationId = editForm.locationId;
    if (editForm.autoPaidOnPresence !== original.autoPaidOnPresence) body.autoPaidOnPresence = editForm.autoPaidOnPresence;
    if (editForm.memo !== original.memo) body.memo = editForm.memo; // leerer String löscht das Memo
    const rows = matrixRowsToInputs(editForm.rows, original.rows, { showRelease: true });
    if (rows.length > 0) body.participantGroupSettings = rows;
    return body;
  };

  const isCurrentEditPopup = (eventId) => {
    const state = popupStateRef.current;
    return state.isPopupOpen && state.popupData?.name === 'editevent' && state.popupData?.identifier === eventId;
  };

  const submitUpdate = (eventId, body) => {
    const requestId = ++editRequestRef.current;
    setBusy('save');
    setEditError('');
    startSuccessBarLoading("Änderungen werden übernommen");
    api.put(`/Event/Update/${eventId}`, body)
      .then((response) => {
        startSuccessBarSuccess("Änderungen übernommen");
        if (response.data?.id != null) updatePost(response.data);
        if (editRequestRef.current !== requestId) return;
        setBusy(null);
        if (isCurrentEditPopup(eventId)) closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        if (editRequestRef.current !== requestId) return;
        setBusy(null);
        setEditError(apiErrorMessage(err, 'Änderungen konnten nicht übernommen werden.'));
      });
  };

  // Preis- oder Freigabe-Änderungen: erst die Auswirkungen zeigen (gleicher Body), dann speichern.
  const loadUpdatePreview = (eventId, body) => {
    const requestId = ++editRequestRef.current;
    setBusy('preview');
    setEditError('');
    api.post(`/Event/${eventId}/PreviewUpdate`, body)
      .then(res => {
        if (editRequestRef.current !== requestId) return;
        setBusy(null);
        setPreview({ body, data: res.data });
        setEditStep('confirm');
      })
      .catch(err => {
        if (editRequestRef.current !== requestId) return;
        setBusy(null);
        setEditError(apiErrorMessage(err, 'Auswirkungen konnten nicht ermittelt werden.'));
      });
  };

  const handleEditEventConfirm = () => {
    if (busy || editForm.eventId == null) return;
    const eventId = editForm.eventId;

    if (editStep === 'confirm' && preview) {
      submitUpdate(eventId, preview.body);
      return;
    }

    const body = buildUpdateBody();
    if (!body) return;
    if (Object.keys(body).length === 0) {
      closePopup();
      return;
    }
    if (body.participantGroupSettings) {
      loadUpdatePreview(eventId, body);
      return;
    }
    submitUpdate(eventId, body);
  };

  const backToEditForm = () => {
    if (busy === 'save') return;
    editRequestRef.current += 1;
    setBusy(null);
    setEditError('');
    setEditStep('form');
  };

  function formatTimeRange(time, timeEnd) {
    const fmt = t => t ? t.substring(0, 5) : null;
    const from = fmt(time);
    const to = fmt(timeEnd);
    if (!from) return null;
    return to ? `${from} - ${to} Uhr` : `${from} Uhr`;
  };

  // ── Info-Popup ──
  const infoRules = eventRules(info);
  const infoMapLink = mapLinkFor(info.location);
  const infoSettings = Array.isArray(info.participantGroupSettings) ? info.participantGroupSettings : null;
  const infoSettingsById = new Map((infoSettings ?? []).map(s => [Number(s.participantGroupId), s]));
  const infoGroups = groupsOfSettings([...infoSettingsById.keys()], groupsById);
  const infoReleasedGroups = infoGroups.filter(g => infoSettingsById.get(g.id)?.released);
  const infoReleasedFor = releaseSummary(infoSettings, groupsById);
  // Anmelde-Zusätze des Prozesses (EventAddOnSummary: Preis + Gruppenpreise, Höchstmenge) — ohne Prozess keine.
  const infoAddOns = Array.isArray(info.addOns) ? info.addOns : [];
  const infoStatusLabel = eventStatusLabel(info.state, { event: info, releasedFor: infoReleasedFor });
  const infoTemplateName = infoRules.templateName && infoRules.templateName !== infoRules.eventLabel ? infoRules.templateName : null;

  // ── Bestätigung (PreviewUpdate) ──
  const editPost = posts.find(p => p.id === editForm.eventId) ?? null;
  const previewData = preview?.data ?? null;
  const previewGroups = (previewData?.groups ?? []).map(g => ({ ...g, group: groupsById.get(Number(g.participantGroupId)) ?? null }));
  const changedPreviewGroups = previewGroups
    .filter(g => !samePrice(g.oldEntryPrice, g.newEntryPrice)
      || g.wasReleased !== g.willBeReleased
      || g.repricedParticipants > 0)
    .sort((a, b) => (a.group && b.group ? compareParticipantGroups(a.group, b.group) : 0));
  const previewCurrentFor = releaseSummary(previewGroups.map(g => ({ participantGroupId: g.participantGroupId, released: g.wasReleased })), groupsById);
  const previewProjectedFor = releaseSummary(previewGroups.map(g => ({ participantGroupId: g.participantGroupId, released: g.willBeReleased })), groupsById);
  const previewStateChanged = previewData != null && previewData.currentState !== previewData.projectedState;
  const isConfirmStep = editStep === 'confirm' && previewData != null;

  const editConfirmText = isConfirmStep
    ? (busy === 'save' ? 'Speichert …' : 'Speichern')
    : (busy === 'preview' ? 'Prüft …' : busy === 'save' ? 'Speichert …' : 'Übernehmen');

  // ── Absage-Popup ──
  const cancelGroups = (cancelPreview.data?.groups ?? [])
    .filter(g => g.participants > 0)
    .map(g => ({ ...g, group: groupsById.get(Number(g.participantGroupId)) ?? null }))
    .sort((a, b) => (a.group && b.group ? compareParticipantGroups(a.group, b.group) : 0));
  const cancelMailGroups = cancelGroups.filter(g => g.mailEnabled);
  const cancelNoMailGroups = cancelGroups.filter(g => !g.mailEnabled);

  // ── Aktivieren-Popup ──
  const activateKeptFor = releaseSummary(activateState.keptIds.map(id => ({ participantGroupId: id, released: true })), groupsById);

  return (
    <React.Fragment>
      <ContextMenu type={type}>
        <ContextMenuItem label={'Info'} icon={'question_mark'} type={0} onClick={showEventInfo} />
        {isAdmin() && <ContextMenuItem label={'Bearbeiten'} icon={'edit'} type={0} onClick={editEvent} disabled={isOffline} />}
        {canToggleSignup && (currentPost.signupClosed ? (
          <ContextMenuItem label={'Anmeldung öffnen'} icon={'lock_open'} type={2} onClick={() => updateSignupClosed(false)} disabled={isOffline} />
        ) : (
          <ContextMenuItem label={'Anmeldung schließen'} icon={'lock_person'} type={0} onClick={() => openPopup('confirmclosesignup', currentPost.id)} disabled={isOffline} />
        ))}
        {isAdmin() && <ContextMenuItem label={'Rundmail'} icon={'mail'} type={3} onClick={selectRoundmail} disabled={isOffline} />}
        <ContextMenuItem label={'Statistiken'} icon={'show_chart'} type={0} onClick={showAnalytics} />
        {isAdmin() && (isCancelled ? (
          <ContextMenuItem label={'Aktivieren'} icon={'event_available'} type={2} onClick={openActivatePopup} disabled={isOffline} />
        ) : (
          <ContextMenuItem label={'Absagen'} icon={'event_busy'} type={1} onClick={openCancelPopup} disabled={isOffline} />
        ))}
        {isAdmin() && <ContextMenuItem label={'Löschen'} icon={'delete_forever'} type={1} onClick={() => openPopup('permanentdeleteevent', currentPost.id)} disabled={isOffline} />}
      </ContextMenu>
      <Popup name='confirmdeleteevent' confirmText='Absagen' headerText='Event absagen'
        type="confirmaction" onConfirm={handleConfirmDelete} withCancel
      >
        <p>Bist du sicher, dass du das Event absagen möchtest?</p>
        <h3>Absage-Mail</h3>
        {cancelPreview.status === 'loading' && <p className={"eventInfoSub"}>Empfänger werden ermittelt …</p>}
        {cancelPreview.status === 'error' && (
          <p className={"eventInfoSub"}>Die Empfänger konnten nicht ermittelt werden. Die Absage-Mail geht an die bestätigten Teilnehmer, deren Gruppe sie vorsieht.</p>
        )}
        {cancelPreview.status === 'ready' && (cancelGroups.length === 0 ? (
          <p className={"eventInfoSub"}>Keine bestätigten Teilnehmer – es wird keine Absage-Mail verschickt.</p>
        ) : (
          <div className={"eventCancelSummary"}>
            <p>
              <b>Absage-Mail an: </b>
              {cancelMailGroups.length > 0
                ? cancelMailGroups.map(g => (
                  `${groupCountLabel(g.participants, g.group, g.participantGroupName)}${g.withEmail < g.participants ? ` (davon ${g.withEmail} mit E-Mail)` : ''}`
                )).join(', ')
                : 'niemanden'}
            </p>
            {cancelNoMailGroups.length > 0 && (
              <p className={"eventInfoSub"}>
                Ohne Absage-Mail: {cancelNoMailGroups.map(g => groupCountLabel(g.participants, g.group, g.participantGroupName)).join(', ')}
              </p>
            )}
            <p className={"formFieldHint"}>Gezählt werden bestätigte Teilnehmer; ob eine Gruppe die Mail bekommt, legt ihre Mail-Regel fest.</p>
          </div>
        ))}
      </Popup>
      <Popup name='confirmactivateevent' confirmText='Aktivieren' headerText='Event aktivieren'
        type="info" onConfirm={handleConfirmActivate} withCancel
      >
        <p>
          Das abgesagte Event wird wieder aktiv. Der Status ergibt sich danach aus den Freigaben
          {activateState.requiresLeader ? ' und der Spieltagleitung' : ''}.
        </p>
        {activateKeptFor && <p>Freigegeben bleibt es für <b>{activateKeptFor}</b>.</p>}
        {activateState.inProcess && (
          <p className={"eventInfoSub"}>Freigaben aus bereits ausgeführten Prozessschritten werden automatisch wiederhergestellt.</p>
        )}
      </Popup>
      <Popup name='confirmclosesignup' confirmText='Schließen' headerText='Anmeldung schließen'
        type="confirmaction" onConfirm={handleConfirmCloseSignup} withCancel
      >
        <p>
          Über den <b>Einladungslink</b>
          {personalLinkNames ? <> und die <b>persönlichen Links</b> ({personalLinkNames})</> : null}
          {' '}sind danach keine neuen Anmeldungen mehr möglich.
        </p>
        <p>
          Wer schon angemeldet ist, bleibt eingetragen{personalLinkNames ? ' – über den persönlichen Link ist die Abmeldung weiterhin möglich' : ''}.
          {' '}Über das Kontextmenü kannst du die Anmeldung jederzeit wieder öffnen.
        </p>
      </Popup>
      <Popup name='permanentdeleteevent' confirmText='Dauerhaft löschen' headerText='Spieltag löschen' type="confirmaction" onConfirm={handlePermanentDelete} withCancel>
        <p>Du löschst diesen Spieltag <b>dauerhaft</b>. Diese Aktion kann nicht rückgängig gemacht werden.</p>
        <p>Alle damit zusammenhängenden Daten gehen unwiderruflich verloren, z.&nbsp;B. Teilnehmerlisten, Anmeldungen, Mail-Historien, Zusatz- und Artikel-Buchungen und Wartelisten-Einladungen.</p>
      </Popup>
      <Popup name='event' headerText='Infos' type="info">
        <h3>Memo:</h3>
        <div className={"memoOutput"}>
          {(info.memo ?? '').split('\\n').map((zeile, index) => (
            index % 2 === 0 ? <h4 key={index}>{zeile}</h4> : <p key={index}>{zeile}</p>
          ))}
        </div>
        <h3>Datum:</h3>
        <p>{info.date} {formatTimeRange(info.time, info.timeEnd) && formatTimeRange(info.time, info.timeEnd)}</p>
        <h3>Location:</h3>
        {info.location ? (
          <div className={"eventInfoLocation"}>
            <p>{info.location.name}</p>
            {info.location.address && <p className={"eventInfoSub"}>{info.location.address}</p>}
            {infoMapLink && (
              <a className={"eventInfoMapLink"} href={infoMapLink} target="_blank" rel="noopener noreferrer">
                <span className="material-symbols-rounded" aria-hidden="true">map</span>
                In Google Maps öffnen
              </a>
            )}
          </div>
        ) : (
          <p className={"eventInfoSub"}>Keine Location hinterlegt</p>
        )}
        {infoGroups.length > 0 && (
          <React.Fragment>
            <h3>Preise:</h3>
            <ul className={"eventInfoPrices"}>
              {infoGroups.map(g => {
                const s = infoSettingsById.get(g.id);
                return (
                  <li key={g.id} className={"eventInfoPriceRow"}>
                    <span className={"eventInfoPriceGroup"}>
                      <ParticipantGroupDot participantGroup={g} withTooltip={false} />
                      <span>{g.name}</span>
                    </span>
                    {/* Eintritt + je Anmelde-Zusatz der Preis dieser Gruppe (Gruppenpreis, sonst Standardpreis der
                        Prozess-Zeile) — Teile brechen nur zwischen den Beträgen um. */}
                    <span className={"eventInfoPriceValue"}>
                      <span className={"eventInfoPricePart"}>Eintritt {formatEuro(s?.entryPrice)}</span>
                      {infoAddOns.map(a => (
                        <span key={a.addOnId} className={"eventInfoPricePart"}>
                          {' · '}{a.name} {formatEuro(priceFor(a, g.id) ?? 0)}
                        </span>
                      ))}
                    </span>
                  </li>
                );
              })}
            </ul>
          </React.Fragment>
        )}
        <h3>Bezahlung:</h3>
        <p>Bei Anwesenheit automatisch bezahlt: <b>{info.autoPaidOnPresence ? 'an' : 'aus'}</b></p>
        <h3>Zusätze:</h3>
        {infoAddOns.length > 0 ? (
          <ul className={"eventInfoAddOns"}>
            {infoAddOns.map(a => (
              <li key={a.addOnId} className={"eventInfoAddOnRow"}>
                <span className={"eventInfoAddOnName"}>{a.name}</span>
                <span className={"eventInfoAddOnMeta"}>{formatMax(a.maxQuantity)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={"eventInfoSub"}>{info.processId != null ? 'Keine Zusätze in diesem Prozess' : 'Keine Zusätze – der Spieltag gehört zu keinem Prozess'}</p>
        )}
        <h3>Art:</h3>
        <p>{infoRules.eventLabel}</p>
        {infoTemplateName && <p className={"eventInfoSub"}>Vorlage „{infoTemplateName}“</p>}
        {infoRules.linkSignup && (
          <p className={"eventInfoSub"}>
            Gäste melden sich nur über den Einladungslink an.
            {personalLinkNames && ` ${personalLinkNames} sehen es über ihren persönlichen Link, sobald es für sie freigegeben ist.`}
          </p>
        )}
        {infoRules.linkSignup && (
          <React.Fragment>
            <h3>Anmeldung:</h3>
            <p>{info.signupClosed ? 'Geschlossen – keine neuen Anmeldungen über Einladungslink und persönliche Links' : 'Offen – Anmeldung über den Einladungslink'}</p>
          </React.Fragment>
        )}
        {infoSettings && infoGroups.length > 0 && (
          <React.Fragment>
            <h3>Freigegeben für:</h3>
            {infoReleasedGroups.length > 0 ? (
              <div className={"participantGroupChips"}>
                {infoReleasedGroups.map(g => <ParticipantGroupChip key={g.id} group={g} label={g.pluralName} />)}
              </div>
            ) : (
              <p className={"eventInfoSub"}>Noch für keine Gruppe freigegeben</p>
            )}
          </React.Fragment>
        )}
        <h3>Status:</h3>
        <div className={"eventInfoStatus"}>
          <EventStatus statusId={info.state} event={info} releasedFor={infoReleasedFor} single compact />
          {infoStatusLabel && <span>{infoStatusLabel}</span>}
        </div>
      </Popup>
      <Popup
        name='editevent'
        headerText={isConfirmStep ? 'Änderungen bestätigen' : 'Spieltag bearbeiten'}
        confirmText={editConfirmText}
        onConfirm={handleEditEventConfirm}
        withCancel
        dontCloseOnConfirm
        wide
        type="info"
      >
        {/* display: contents — Marker für die breitere Popup-Variante (EventContextMenu.css), die
            Kinder bleiben Grid-Items von .infoContainer. */}
        <div className={"eventEditPopup"}>
          <p ref={editTopRef} className={"eventEditSubtitle"}>{formatDateDe(editForm.date)} · {editForm.eventLabel}</p>
          {editError && <p className={"eventEditError"}>{editError}</p>}

          {isConfirmStep ? (
            <React.Fragment>
              <h3>Status</h3>
              <div className={"eventPreviewState"}>
                <span className={"eventPreviewStateItem"}>
                  <EventStatus statusId={previewData.currentState} event={editPost} releasedFor={previewCurrentFor} single compact />
                  <span>{eventStatusLabel(previewData.currentState, { event: editPost, releasedFor: previewCurrentFor })}</span>
                </span>
                {previewStateChanged ? (
                  <React.Fragment>
                    <span className={"material-symbols-rounded eventPreviewArrow"} aria-label="wird zu">arrow_forward</span>
                    <span className={"eventPreviewStateItem"}>
                      <EventStatus statusId={previewData.projectedState} event={editPost} releasedFor={previewProjectedFor} single compact />
                      <span>{eventStatusLabel(previewData.projectedState, { event: editPost, releasedFor: previewProjectedFor })}</span>
                    </span>
                  </React.Fragment>
                ) : (
                  <span className={"eventPreviewMuted"}>(unverändert)</span>
                )}
              </div>

              <h3>Gruppen</h3>
              {changedPreviewGroups.length === 0 ? (
                <p className={"eventInfoSub"}>Keine Änderungen an Preisen oder Freigaben.</p>
              ) : (
                <ul className={"eventPreviewGroups"}>
                  {changedPreviewGroups.map(g => {
                    const entryChanged = !samePrice(g.oldEntryPrice, g.newEntryPrice);
                    const n = g.repricedParticipants;
                    return (
                      <li key={g.participantGroupId} className={"eventPreviewGroup"}>
                        <span className={"eventPreviewGroupName"}>
                          {g.group && <ParticipantGroupDot participantGroup={g.group} withTooltip={false} />}
                          <span>{g.group?.name ?? g.participantGroupName}</span>
                        </span>
                        {entryChanged && (
                          <span className={"eventPreviewLine"}>
                            Eintritt <span className={"eventPreviewOld"}>{formatEuro(g.oldEntryPrice)}</span> → <b>{formatEuro(g.newEntryPrice)}</b>
                          </span>
                        )}
                        {g.wasReleased !== g.willBeReleased && (
                          <span className={`eventPreviewLine eventPreviewRelease ${g.willBeReleased ? 'isOn' : 'isOff'}`}>
                            <span className={"material-symbols-rounded"} aria-hidden="true">{g.willBeReleased ? 'visibility' : 'visibility_off'}</span>
                            {g.willBeReleased ? 'Wird freigegeben' : 'Freigabe wird zurückgenommen'}
                          </span>
                        )}
                        {(entryChanged || n > 0) && (
                          <span className={"eventPreviewLine eventPreviewMuted"}>
                            {n === 0
                              ? 'Keine offenen Teilnehmer betroffen'
                              : n === 1
                                ? '1 offener Teilnehmer wird neu berechnet'
                                : `${n} offene Teilnehmer werden neu berechnet`}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {previewData.warnings?.length > 0 && (
                <div className={"eventEditWarning"}>
                  <ul className={"eventPreviewWarnings"}>
                    {previewData.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <p className={"formFieldHint"}>Bezahlte Teilnehmer und manuell gesetzte Preise bleiben unverändert.</p>
              <div className={"eventPreviewBack"}>
                <ActionButton icon={'arrow_back'} label={'Zurück zur Bearbeitung'} onClick={backToEditForm} disabled={busy === 'save'} />
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <h3>Location</h3>
              <LocationSelect
                label={null}
                locations={locations}
                loading={locationsLoading}
                value={editForm.locationId}
                currentLocation={editForm.location}
                onChange={id => setField('locationId', id)}
                onLocationSaved={handleLocationSaved}
                disabled={editForm.inProcess}
              />
              {editForm.inProcess && (
                <p className={"formFieldHint"}>Ort kommt vom Prozess – zum Wechseln das Event aus dem Prozess nehmen.</p>
              )}

              {/* Anmelde-Zusätze gehören zum Prozess (Preis, Höchstmenge, Homepage) — hier nur zur Orientierung. */}
              <h3>Zusätze</h3>
              {editForm.addOns.length > 0 && (
                <AddOnChips items={addOnCounterItems(editForm.addOns)} ariaLabel={'Zusätze des Prozesses'} />
              )}
              <p className={"formFieldHint"}>
                {editForm.addOns.length > 0
                  ? 'Zusätze werden im Prozess gepflegt.'
                  : editForm.inProcess
                    ? 'Keine Zusätze in diesem Prozess – Zusätze werden im Prozess gepflegt.'
                    : 'Ohne Prozess gibt es keine Anmelde-Zusätze.'}
              </p>

              <h3>Gruppen</h3>
              {!editForm.hasSettings ? (
                <p className={"formFieldHint"}>Für dieses Event liegen keine Gruppen-Preise vor – bitte die Seite neu laden.</p>
              ) : editGroups.length === 0 ? (
                <p className={"formFieldHint"}>
                  {groupsFailed || groupsLoaded ? 'Gruppen konnten nicht geladen werden – bitte die Seite neu laden.' : 'Gruppen werden geladen …'}
                </p>
              ) : (
                <React.Fragment>
                  <ParticipantGroupPriceMatrix
                    groups={editGroups}
                    rows={editForm.rows}
                    onChange={setRow}
                    showRelease
                    releaseLockedReason={editReleaseLockedReason}
                    disabled={busy != null}
                    ariaLabel={'Preise und Freigabe je Gruppe'}
                  />
                  <p className={"formFieldHint"}>
                    „Freigegeben“ macht das Event für die Gruppe sichtbar – für Gäste heißt das: öffentlich auf der Homepage und im Anmeldeformular.
                    Bei Preisänderungen werden offene (unbezahlte) Teilnehmer der Gruppe neu berechnet; vor dem Speichern siehst du die Auswirkungen.
                  </p>
                </React.Fragment>
              )}

              <h3>Bezahlung</h3>
              <CustomTogglebox
                placeholder={'Bei Anwesenheit automatisch bezahlt'}
                checked={editForm.autoPaidOnPresence}
                onChange={e => setField('autoPaidOnPresence', e.target.checked)}
              />
              <p className={"formFieldHint"}>Beim Einschalten gelten bereits anwesende, offene Spieler sofort als bezahlt.</p>

              {editForm.linkSignup && (
                <React.Fragment>
                  <h3>Einladungslink</h3>
                  <div className={"eventLinkRow"}>
                    <input
                      className={"eventLinkInput"}
                      readOnly
                      aria-label="Einladungslink"
                      value={link.status === 'loading' ? 'Link wird geladen …' : link.url}
                      onFocus={e => e.target.select()}
                    />
                    <ActionButton
                      icon={copied ? 'check' : 'content_copy'}
                      label={copied ? 'Kopiert' : 'Kopieren'}
                      type={copied ? 2 : 3}
                      onClick={handleCopyLink}
                      disabled={!link.url}
                    />
                  </div>
                  {link.status === 'error' && (
                    <div className={"eventLinkErrorRow"}>
                      <p className={"eventLinkFeedback error"}>{link.error}</p>
                      <ActionButton icon={'refresh'} label={'Erneut laden'} onClick={() => loadLink(editForm.eventId)} disabled={isOffline} />
                    </div>
                  )}
                  {linkFeedback && <p className={`eventLinkFeedback ${linkFeedback.tone}`}>{linkFeedback.text}</p>}
                  {confirmRegenerate ? (
                    <div className={"eventLinkConfirm"}>
                      <p>Der bisherige Link wird <b>sofort ungültig</b> – bereits verschickte Links funktionieren dann nicht mehr.</p>
                      <div className={"eventLinkConfirmActions"}>
                        <ActionButton label={'Abbrechen'} onClick={() => setConfirmRegenerate(false)} disabled={regenerating} />
                        <ActionButton
                          icon={'autorenew'}
                          label={regenerating ? 'Wird erzeugt …' : 'Neu erzeugen'}
                          type={1}
                          onClick={handleRegenerateLink}
                          disabled={regenerating || isOffline}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={"eventLinkActions"}>
                      <ActionButton
                        icon={'autorenew'}
                        label={'Neuen Link erzeugen'}
                        type={4}
                        onClick={() => { setConfirmRegenerate(true); setLinkFeedback(null); }}
                        disabled={link.status === 'loading' || isOffline}
                      />
                    </div>
                  )}
                </React.Fragment>
              )}

              <h3>Memo</h3>
              <textarea className={"memoInput"} maxLength={200} placeholder='Event-Memo...' value={editForm.memo} onChange={e => setField('memo', e.target.value)}></textarea>
            </React.Fragment>
          )}
        </div>
      </Popup>
      <Popup name='eventerror' confirmText='Ok' headerText='Error' type="error">
        <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
      </Popup>
    </React.Fragment>
  );
}

export default EventContextMenu;
